"use client";

import React from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { X } from "lucide-react";
import type { SimpleTableConfig, BaseEntity } from "./types";

// ============================================================================
// TYPES
// ============================================================================

interface SimpleTableMobileBulkActionsProps<T extends BaseEntity> {
  config: SimpleTableConfig<T>;
  selectedItems: string[];
  onBulkAction: (actionId: string) => void;
  onClearSelection: () => void;
  isLoading?: string | null;
}

// ============================================================================
// MOBILE BULK ACTIONS COMPONENT
// ============================================================================

export function SimpleTableMobileBulkActions<T extends BaseEntity>({
  config,
  selectedItems,
  onBulkAction,
  onClearSelection,
  isLoading,
}: SimpleTableMobileBulkActionsProps<T>) {
  // Don't render if no items selected
  if (selectedItems.length === 0) {
    return null;
  }

  // Get available bulk actions from config
  const availableActions = config.bulkActions || [];

  return (
    <div className="fixed bottom-4 left-4 right-4 z-50">
      <div className="bg-card border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Badge variant="secondary" className="text-sm">
              {selectedItems.length} selected
            </Badge>
          </div>

          <Button
            variant="ghost"
            size="sm"
            onClick={onClearSelection}
            className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 p-1" title="Close"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-2 overflow-x-auto">
          {availableActions.map((action) => (
            <Button
              key={action.id}
              variant={action.variant === "destructive" ? "destructive" : "outline"}
              size="sm"
              onClick={() => onBulkAction(action.id)}
              disabled={isLoading === action.id}
              className="flex-shrink-0"
            >
              {isLoading === action.id ? (
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                  Processing...
                </div>
              ) : (
                action.label
              )}
            </Button>
          ))}

          {/* Clear Selection Button */}
          <Button
            variant="ghost"
            size="sm"
            onClick={onClearSelection}
            className="flex-shrink-0 text-muted-foreground"
          >
            Clear
          </Button>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// ALTERNATIVE: STICKY BOTTOM BAR VERSION
// ============================================================================

export function SimpleTableMobileBulkActionsSticky<T extends BaseEntity>({
  config,
  selectedItems,
  onBulkAction,
  onClearSelection,
}: SimpleTableMobileBulkActionsProps<T>) {
  // Don't render if no items selected
  if (selectedItems.length === 0) {
    return null;
  }

  // Get available bulk actions from config
  const availableActions = config.bulkActions || [];

  return (
    <div className="sticky bottom-0 bg-card border-t border-gray-200 dark:border-gray-700 p-4 shadow-lg">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Badge variant="secondary" className="text-sm">
            {selectedItems.length} selected
          </Badge>
        </div>

        <Button
          variant="ghost"
          size="sm"
          onClick={onClearSelection}
          className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300" title="Close"
        >
          <X className="h-4 w-4" />
        </Button>
      </div>

      {/* Action Buttons */}
      <div className="flex gap-2 overflow-x-auto">
        {availableActions.map((action) => (
          <Button
            key={action.id}
            variant={action.variant === "destructive" ? "destructive" : "outline"}
            size="sm"
            onClick={() => onBulkAction(action.id)}
            className="flex-shrink-0"
          >
            {action.label}
          </Button>
        ))}

        {/* Clear Selection Button */}
        <Button
          variant="ghost"
          size="sm"
          onClick={onClearSelection}
          className="flex-shrink-0 text-gray-500"
        >
          Clear
        </Button>
      </div>
    </div>
  );
}
